"use client";

import { useMemo } from "react";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
} from "recharts";
import { ChartConfig, ChartContainer } from "@/shared/components/ui/chart";

type ChartDataItem = {
  month: string;
  background: number;
  red: number;
  orange: number;
  yellow: number;
};

type BarChartProps = {
  data?: ChartDataItem[];
};

const chartConfig = {
  red: {
    label: "Red",
    color: "#f76b6d",
  },
  orange: {
    label: "Orange",
    color: "#f7876b",
  },
  yellow: {
    label: "Yellow",
    color: "#f9cf6c",
  },
} satisfies ChartConfig;

// Default mock data - Figma'dan alınan değerler (normalize edilmiş - max 60K)
const defaultData: ChartDataItem[] = [
  { month: "J", background: 0, red: 14, orange: 6, yellow: 10 },
  { month: "F", background: 0, red: 2, orange: 16, yellow: 8 },
  { month: "M", background: 0, red: 14, orange: 8, yellow: 12 },
  { month: "A", background: 0, red: 6, orange: 14, yellow: 6 },
  { month: "M", background: 0, red: 15, orange: 10, yellow: 4 },
  { month: "J", background: 0, red: 15, orange: 9, yellow: 6 },
  { month: "J", background: 0, red: 16, orange: 8, yellow: 8 },
  { month: "A", background: 0, red: 9, orange: 10, yellow: 10 },
  { month: "S", background: 0, red: 8, orange: 12, yellow: 8 },
  { month: "O", background: 0, red: 8, orange: 10, yellow: 10 },
  { month: "N", background: 0, red: 14, orange: 7, yellow: 8 },
  { month: "D", background: 0, red: 8, orange: 12, yellow: 14 },
];

// Custom bar shape with rounded corners and gap between segments
const CustomBar = (props: any) => {
  const { x, y, width, height, fill, isTop, isBottom, payload, dataKey } =
    props;

  // Gap between segments (1px white space)
  const gap = 2;

  // All segments have gap at the top to create white space between them
  const yOffset = gap;
  const adjustedHeight = height - gap;
  const adjustedY = y + yOffset;

  // Calculate rounded corners - only bottom segment (yellow) has rounded corners
  const radius = 4;
  let path = "";

  if (isBottom) {
    // Bottom segment (yellow) - only bottom corners rounded
    // Start from top-left, go clockwise
    path = `M ${x} ${adjustedY}
            L ${x + width} ${adjustedY}
            L ${x + width} ${adjustedY + adjustedHeight - radius}
            Q ${x + width} ${adjustedY + adjustedHeight} ${
      x + width - radius
    } ${adjustedY + adjustedHeight}
            L ${x + radius} ${adjustedY + adjustedHeight}
            Q ${x} ${adjustedY + adjustedHeight} ${x} ${
      adjustedY + adjustedHeight - radius
    }
            Z`;
  } else {
    // Other segments (orange, red) - no rounded corners
    path = `M ${x} ${adjustedY}
            L ${x} ${adjustedY + adjustedHeight}
            L ${x + width} ${adjustedY + adjustedHeight}
            L ${x + width} ${adjustedY} Z`;
  }

  return <path d={path} fill={fill} />;
};

// Custom bar component wrapper
const renderCustomBar = (
  dataKey: string,
  isTop: boolean,
  isBottom: boolean
) => {
  return (props: any) => {
    return (
      <CustomBar
        {...props}
        isTop={isTop}
        isBottom={isBottom}
        dataKey={dataKey}
      />
    );
  };
};

export default function BarChart({ data }: BarChartProps) {
  // Process and sort data
  const processedData = useMemo(() => {
    const rawData = data || defaultData;

    // Calculate background values
    const withBackground = rawData.map((item) => ({
      ...item,
      background: 60 - (item.red + item.orange + item.yellow),
    }));

    // Sort by month order
    const monthOrder = [
      "J",
      "F",
      "M",
      "A",
      "M",
      "J",
      "J",
      "A",
      "S",
      "O",
      "N",
      "D",
    ];
    return [...withBackground].sort((a, b) => {
      const indexA = monthOrder.indexOf(a.month);
      const indexB = monthOrder.indexOf(b.month);
      return indexA - indexB;
    });
  }, [data]);

  return (
    <ChartContainer
      config={chartConfig}
      className="w-full h-[340px] ml-[-20px]"
    >
      <RechartsBarChart
        data={processedData}
        margin={{
          left: 38,
          right: 0,
          top: 0,
          bottom: 20,
        }}
        barCategoryGap="25.6px"
        barGap={0}
        barSize={32}
      >
        <XAxis
          dataKey="month"
          tick={{ fill: "#71717a", fontSize: 12, dy: 10 }}
          axisLine={false}
          tickLine={false}
          height={20}
        />
        <YAxis
          tick={{ fill: "#71717a", fontSize: 12, dx: 10 }}
          axisLine={false}
          tickLine={false}
          width={30}
          ticks={[0, 15, 30, 45, 60]}
          tickFormatter={(value) => `${value}K`}
          domain={[0, 60]}
        />

        {/* Colored stacked bars - rendered after to appear on top */}
        <Bar
          dataKey="yellow"
          stackId="a"
          fill="#f9cf6c"
          shape={renderCustomBar("yellow", false, true)}
          barSize={32}
        />
        <Bar
          dataKey="orange"
          stackId="a"
          fill="#f7876b"
          shape={renderCustomBar("orange", false, false)}
          barSize={32}
        />
        <Bar
          dataKey="red"
          stackId="a"
          fill="#f76b6d"
          shape={renderCustomBar("red", false, false)}
          barSize={32}
        />
        {/* Background bar - rendered first to appear behind colored bars */}
        <Bar
          dataKey="background"
          stackId="a"
          fill="rgba(228, 228, 231, 0.4)"
          radius={[4, 4, 0, 0]}
          isAnimationActive={false}
          barSize={32}
        />
      </RechartsBarChart>
    </ChartContainer>
  );
}
